import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Share2,
  Star,
  Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePrompt } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";
import type { SavedViewRead } from "@/api/types";

interface ViewsDropdownProps {
  views: SavedViewRead[];
  loading: boolean;
  activeView: SavedViewRead | null;
  canCreate: boolean;
  canShare: boolean;
  onPickView: (view: SavedViewRead) => void;
  onRename: (view: SavedViewRead, name: string) => Promise<void>;
  onToggleShared: (view: SavedViewRead) => Promise<void>;
  onToggleDefault: (view: SavedViewRead) => Promise<void>;
  onDelete: (view: SavedViewRead) => Promise<void>;
  /** Fired when the user clicks "+ Create new view" inside the
   *  popover. The parent owns the SaveAsNewViewModal's open state
   *  so it can close this popover before the modal opens — keeps
   *  Radix from stacking two open layers. */
  onCreateNewClick: () => void;
}

/**
 * F6 dropdown that replaces the F3 horizontal pill row of saved
 * views. Search + scrollable list + per-row ⋯ menu + "+ Create new
 * view" footer button. Trigger button label shows the active view
 * (with ★ when default) or "No view selected" in muted text.
 *
 * Architecture choice — Popover (not DropdownMenu): DropdownMenuItem
 * auto-closes on click, which would dismiss the popover whenever
 * the per-row ⋯ menu opens. Popover gives us free control over
 * closure so the per-row menus + the SaveAsNewViewModal trigger
 * can coexist.
 */
export function ViewsDropdown({
  views,
  loading,
  activeView,
  canCreate,
  canShare,
  onPickView,
  onRename,
  onToggleShared,
  onToggleDefault,
  onDelete,
  onCreateNewClick,
}: ViewsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Delete-confirm dialog state — was inside ViewsBar before F6,
  // now lives here alongside the new dropdown.
  const [confirmDelete, setConfirmDelete] = useState<SavedViewRead | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const prompt = usePrompt();

  // Reset the search box every time the dropdown opens. Keeping
  // the previous search would confuse the user on the next visit.
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  // Sorted + filtered view list. Defaults: is_default first within
  // "my" views, then alphabetical; shared views below my views, also
  // alphabetical. Same ordering F3 ViewsBar used.
  const filtered = useMemo<SavedViewRead[]>(() => {
    const q = search.trim().toLowerCase();
    const myViews = views.filter((v) => v.is_mine).slice();
    const sharedViews = views.filter((v) => !v.is_mine).slice();
    myViews.sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return a.name.localeCompare(b.name);
    });
    sharedViews.sort((a, b) => a.name.localeCompare(b.name));
    const combined = [...myViews, ...sharedViews];
    if (!q) return combined;
    return combined.filter((v) => v.name.toLowerCase().includes(q));
  }, [views, search]);

  async function askRename(view: SavedViewRead) {
    setOpen(false);
    const next = await prompt({
      title: "Rename view",
      description: `Rename "${view.name}" to:`,
      defaultValue: view.name,
      confirmLabel: "Rename",
    });
    if (!next || next === view.name) return;
    try {
      await onRename(view, next);
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  async function handleToggleDefault(view: SavedViewRead) {
    try {
      await onToggleDefault(view);
      toast.success(
        view.is_default ? "Default removed" : "Set as default",
      );
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  async function handleToggleShared(view: SavedViewRead) {
    try {
      await onToggleShared(view);
      toast.success(view.is_shared ? "View made private" : "View shared");
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await onDelete(confirmDelete);
      setConfirmDelete(null);
      toast.success(`View "${confirmDelete.name}" deleted`);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-w-[180px] justify-between text-slate-700"
          >
            <span className="inline-flex items-center gap-1.5">
              {activeView ? (
                <>
                  {activeView.is_default && (
                    <Star
                      className="h-3 w-3 fill-amber-400 text-amber-400"
                      aria-hidden
                    />
                  )}
                  <span className="truncate">{activeView.name}</span>
                  {activeView.is_shared && (
                    <UsersIcon
                      className="h-3 w-3 shrink-0 text-slate-400"
                      aria-label="Shared with team"
                    />
                  )}
                </>
              ) : (
                <span className="text-slate-500">No view selected</span>
              )}
            </span>
            <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-slate-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[300px] p-0"
          // Per-row ⋯ menus + the prompt modal portal outside this
          // Popover's subtree. Keep the popover open while the user
          // interacts with them; only true outside clicks close.
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest("[data-radix-popper-content-wrapper]")) {
              e.preventDefault();
            }
          }}
        >
          <div className="relative border-b border-slate-900/[0.06] p-2">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search views…"
              className="h-8 border-slate-200 pl-8 text-xs"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {loading && views.length === 0 ? (
              <p className="flex items-center justify-center gap-1.5 px-3 py-4 text-xs text-slate-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading views…
              </p>
            ) : views.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs italic text-slate-400">
                No saved views yet.
              </p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-slate-400">
                No views match.
              </p>
            ) : (
              <ul>
                {filtered.map((v) => {
                  const isActive = activeView?.id === v.id;
                  return (
                    <li
                      key={v.id}
                      className={cn(
                        "group flex items-center gap-1.5 px-2 py-1",
                        isActive && "bg-slate-100",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          onPickView(v);
                          setOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-1 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        {v.is_default && (
                          <Star
                            className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400"
                            aria-label="Default view"
                          />
                        )}
                        <span className="truncate">{v.name}</span>
                        {v.is_shared && (
                          <UsersIcon
                            className="h-3 w-3 shrink-0 text-slate-400"
                            aria-label={
                              v.is_mine
                                ? "Shared with team"
                                : `Shared by ${v.user_name ?? "teammate"}`
                            }
                          />
                        )}
                      </button>
                      {v.is_mine && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={`Actions for ${v.name}`}
                              className={cn(
                                "flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 transition-colors",
                                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                                "hover:bg-slate-200 hover:text-slate-700",
                              )}
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem
                              onSelect={() => void handleToggleDefault(v)}
                            >
                              <Star className="mr-2 h-3.5 w-3.5" />
                              {v.is_default
                                ? "Remove default"
                                : "Set as default"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => void askRename(v)}
                            >
                              <Pencil className="mr-2 h-3.5 w-3.5" />
                              Rename
                            </DropdownMenuItem>
                            {canShare && (
                              <DropdownMenuItem
                                onSelect={() => void handleToggleShared(v)}
                              >
                                <Share2 className="mr-2 h-3.5 w-3.5" />
                                {v.is_shared
                                  ? "Make private"
                                  : "Share with team"}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => setConfirmDelete(v)}
                              className="text-rose-600 focus:bg-rose-50 focus:text-rose-700"
                            >
                              <Trash2 className="mr-2 h-3.5 w-3.5" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {canCreate && (
            <div className="border-t border-slate-900/[0.06] p-2">
              <button
                type="button"
                onClick={() => {
                  // Close the dropdown BEFORE the parent opens the
                  // modal — avoids stacked popovers fighting for
                  // outside-click priority.
                  setOpen(false);
                  onCreateNewClick();
                }}
                className="flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
              >
                <Plus className="h-3.5 w-3.5" />
                Create new view
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(o) => {
          if (deleting) return;
          if (!o) setConfirmDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>
              Delete view &ldquo;{confirmDelete?.name ?? ""}&rdquo;?
            </DialogTitle>
            <DialogDescription>
              {confirmDelete?.is_shared
                ? "This view is shared with the team. Deleting it removes it for everyone."
                : "This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={deleting}
            >
              {deleting && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function extractMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "Couldn't update the view.";
}
